import type _jquery from "jquery";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord,
} from "@mcwd/util-vue";
import { useFormLogger } from "./use-form-logger.js";
import { useFormEvents } from "./use-form-events.js";
import { useFormState } from "./use-form-state.js";
import { useGatingLevel } from "./use-gating-level.js";
import { provide } from "vue";
type UseAbandonmentTriggerInjectionValues = InjectionValuesRecord<{
  beforeUnloadListener: () => void,
  modalAbandonmentListener: () => void,
}>;

type UseAbandonmentTriggerInjectionKeys = InjectionKeysRecord<UseAbandonmentTriggerInjectionValues>;

const UseAbandonmentTriggerInjectionKeys = CreateInjectionKeysRecord<UseAbandonmentTriggerInjectionKeys>({
  beforeUnloadListener: "beforeUnloadListener",
  modalAbandonmentListener: "modalAbandonmentListener"
});
/**
 * Provide the abandonment listeners, you have to still call the individual functions
 */
export function initAbandonmentListeners() {

  if (checkValuesAlreadyProvided(UseAbandonmentTriggerInjectionKeys)) {
    return;
  }
  const { logger } = useFormLogger({ callerLabel: "initBeforeUnloadScript" });
  const { FormState } = useFormState({ callerLabel: "initBeforeUnloadScript" });
  const { formAbandonment, modalClosed, userFirstInteraction } = useFormEvents({ callerLabel: "initBeforeUnloadScript" });
  const { CurrentFormExperienceGatingState } = useGatingLevel({
    callerLabel: "initFormAbandonmentManager",
  });
  /**
   * Triggers form  abandonment event if form is not submitted and user is leaving the page
   */
  function initBeforeUnloadScript() {
    let userFirstInteractionValue = false;
    userFirstInteraction.on(() => {
      userFirstInteractionValue = true;
    });
    const beforeUnloadHandler = () => {
      if (FormState.value === "RegularForm" && CurrentFormExperienceGatingState.value !== "NoGate" && userFirstInteractionValue) {
        formAbandonment.trigger({ eventName: "formAbandonment" });
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    logger.debug("BeforeUnload script initiated.");
  }

  /**
   * Triggers form abandonment event if the form is not submitted and the user closes the modal
   */
  function initModalAbandonmentListner() {
    modalClosed.on(() => {
      if (FormState.value === "RegularForm" && CurrentFormExperienceGatingState.value !== "NoGate") {
        formAbandonment.trigger({ eventName: "formAbandonment" });
      }
    });
    logger.debug("ModelCloseListener initiated.");
  }

  provide(UseAbandonmentTriggerInjectionKeys.beforeUnloadListener, initBeforeUnloadScript);
  provide(UseAbandonmentTriggerInjectionKeys.modalAbandonmentListener, initModalAbandonmentListner);

}
/**
 * Calls the two functins that listen for form abandonment
 */
export function useAbandonmentListeners({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {

    callerLabel ??= "AbandonmentListeners";

    const beforeUnloadListener = injectRequiredWithSelf(UseAbandonmentTriggerInjectionKeys.beforeUnloadListener);
    const modalAbandonmentListener = injectRequiredWithSelf(UseAbandonmentTriggerInjectionKeys.modalAbandonmentListener);
    beforeUnloadListener();
    modalAbandonmentListener();
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? "unknown"}`);
    throw err;
  }
}